import type { ListViewPath } from "_shared/_views/list-pages/current-table-view";
import {
  readListChips,
  writeListChips,
  type ListChipGroup,
  type ListChipSelection,
} from "_shared/_views/list-pages/list-chip-selection";
import type { RetiredListSegment } from "_shared/_views/list-pages/retired-list-redirect";
import type { CurrentTableViewOptions } from "_shared/_views/list-pages/use-current-table-view";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";

/**
 * The tabs and chips of the fee schedules page, and how its URL spells them (#6716).
 *
 * The page had Templates, Active and Archived tabs. Archived listed archived templates and the fee
 * schedules of archived work together, two kinds of thing in one list. Now each kind is a tab --
 * Templates and In use -- and each tab has its own Archived chip, which narrows that tab's list.
 */

/** The path of the page; each tab is a segment under it. */
export const FEE_SCHEDULES_PATH = "/fee-schedules";

/** The query parameter of the Archived chip. */
export const STAGE_PARAMETER = "stage";

/** The Archived chip's value. */
export const ARCHIVED_STAGE = "archived";

export const feeScheduleChipGroups: readonly ListChipGroup[] = [
  { parameter: STAGE_PARAMETER, label: "Stage", chips: [{ value: ARCHIVED_STAGE, label: "Archived" }] },
];

/** The tab a path segment names; Templates for none, or for one the page does not have. */
export function readFeeScheduleTab(segment: string | undefined): PageTab {
  const named = segment?.toLowerCase();
  return Object.values(PageTab).find((tab) => tab === named) ?? PageTab.Templates;
}

/** The path of `tab`. Both tabs keep a segment, as the page's other routes do: `/fee-schedules/templates/edit/:id`. */
export function feeScheduleTabPath(tab: PageTab): string {
  return `${FEE_SCHEDULES_PATH}/${tab}`;
}

/** The chips `searchParams` names. */
export function readFeeScheduleChips(searchParams: URLSearchParams): ListChipSelection {
  return readListChips(feeScheduleChipGroups, searchParams);
}

/** Whether `selection` lists the tab's archive. */
export function listsArchive(selection: ListChipSelection): boolean {
  return (selection[STAGE_PARAMETER] ?? []).includes(ARCHIVED_STAGE);
}

/** `searchParams` with `selection` written over the chips. The list starts again at its first page. */
export function writeFeeScheduleChips(selection: ListChipSelection, searchParams: URLSearchParams): URLSearchParams {
  const next = writeListChips(feeScheduleChipGroups, selection, searchParams);
  if (next.has("pageIndex")) next.set("pageIndex", "0");
  return next;
}

/** The archived templates' list: the Templates tab with its Archived chip. */
export const ARCHIVED_TEMPLATES_PATH = `${feeScheduleTabPath(PageTab.Templates)}?${STAGE_PARAMETER}=${ARCHIVED_STAGE}`;

/**
 * The list tabs the page had before #6716. `/fee-schedules/archived` mostly held archived templates, so
 * it opens the Templates tab's archive; `/fee-schedules/active` listed the fee schedules in use.
 * `/fee-schedules/{active|archived}/:action/:id` are other routes and keep resolving.
 */
export const RETIRED_FEE_SCHEDULE_TABS: readonly RetiredListSegment[] = [
  { segment: "active", tab: PageTab.InUse },
  { segment: "archived", tab: PageTab.Templates, chips: { [STAGE_PARAMETER]: [ARCHIVED_STAGE] } },
];

/** The parameters each tab's stored view keeps besides its chips: its column filters, sort and page size. */
const KEPT_PARAMETERS: Readonly<Record<PageTab, readonly string[]>> = {
  [PageTab.Templates]: ["name", "tags", "orderBy", "orderDir", "pageSize"],
  [PageTab.InUse]: ["orderBy", "orderDir", "pageSize"],
};

/**
 * A stored view as this page still reads it: on a tab the page has, with a chip it no longer has
 * dropped. A view of a retired tab is shown as the tab and chips it became.
 */
export function keepFeeSchedulesView(view: ListViewPath): ListViewPath | undefined {
  const segment = view.tabPath.startsWith(`${FEE_SCHEDULES_PATH}/`)
    ? view.tabPath.slice(FEE_SCHEDULES_PATH.length + 1)
    : undefined;
  if (segment === undefined || segment.includes("/")) return undefined;
  const retired = RETIRED_FEE_SCHEDULE_TABS.find((each) => each.segment === segment);
  const tab = (retired?.tab as PageTab | undefined) ?? Object.values(PageTab).find((each) => each === segment);
  if (tab === undefined) return undefined;

  const stored = new URLSearchParams(view.search);
  const chips = retired?.chips ?? readFeeScheduleChips(stored);
  const kept = writeListChips(feeScheduleChipGroups, chips, new URLSearchParams());
  for (const parameter of KEPT_PARAMETERS[tab]) {
    for (const value of stored.getAll(parameter)) kept.append(parameter, value);
  }
  return { tabPath: feeScheduleTabPath(tab), search: kept.toString() };
}

/**
 * The page's Default view, declared once: the Templates tab with no chip, newest first. A plain visit to
 * `/fee-schedules` opens the reader's starred or last view, else this one.
 */
export const FEE_SCHEDULES_CURRENT_VIEW: CurrentTableViewOptions = {
  tableKey: "fee-schedules",
  basePath: FEE_SCHEDULES_PATH,
  defaultHref: feeScheduleTabPath(PageTab.Templates),
  keepWhatStillExists: keepFeeSchedulesView,
};
